"use client";

import { useEffect, useState } from "react";
import { Button, buttonVariants } from "@/components/ui/button";
import { PendingText } from "@/components/ui/pending-text";
import { authClient } from "@/lib/auth/client";
import {
  LOGIN_ERROR_CODE,
  type LoginErrorCode,
  MAZRICA_ACCOUNT_SWITCH,
} from "@/lib/auth/login-error-codes";
import {
  INVITATION_ACCEPT_FALLBACK_MESSAGE,
  readInvitationErrorMessage,
} from "@/lib/invitations/error-messages";

type PendingAccess = {
  email: string | null;
  invitation: {
    id: string;
    organizationName: string;
    expiresAt: string;
  } | null;
};

const ACCESS_DENIED_HEADLINES: Record<LoginErrorCode, string> = {
  [LOGIN_ERROR_CODE.NO_TENANT_MEMBERSHIP]:
    "このアカウントはまだテナントに参加していません。",
  [LOGIN_ERROR_CODE.TENANT_EXPIRED]:
    "テナントのご利用期間が終了しているため、画面を開けません。",
  [LOGIN_ERROR_CODE.AMBIGUOUS_MEMBERSHIP]:
    "複数のテナントに所属しているため、表示するテナントを決められません。",
};

const ACCESS_DENIED_DETAILS: Record<LoginErrorCode, string> = {
  [LOGIN_ERROR_CODE.NO_TENANT_MEMBERSHIP]:
    "管理者がユーザーを追加すると招待メールが届きます。メール内のリンクから招待を受諾すると利用を開始できます。招待が届いていない場合は管理者へご連絡ください。",
  [LOGIN_ERROR_CODE.TENANT_EXPIRED]: "利用期間の延長は担当者へご連絡ください。",
  [LOGIN_ERROR_CODE.AMBIGUOUS_MEMBERSHIP]:
    "管理者へご連絡ください。所属テナントの整理が必要です。",
};

const expiryFormatter = new Intl.DateTimeFormat("ja-JP", {
  dateStyle: "medium",
  timeStyle: "short",
  timeZone: "Asia/Tokyo",
});

function formatExpiry(value: string) {
  const expiresAt = new Date(value);
  return Number.isNaN(expiresAt.getTime())
    ? null
    : expiryFormatter.format(expiresAt);
}

export function AccessDeniedNotice({ code }: { code: LoginErrorCode }) {
  const { data: session, isPending: isSessionPending } =
    authClient.useSession();
  const [pendingAccess, setPendingAccess] = useState<PendingAccess | null>(
    null,
  );
  const [accepting, setAccepting] = useState(false);
  const [acceptError, setAcceptError] = useState<string | null>(null);
  const [switchingAccount, setSwitchingAccount] = useState(false);

  useEffect(() => {
    if (code !== LOGIN_ERROR_CODE.NO_TENANT_MEMBERSHIP) {
      return;
    }

    const controller = new AbortController();

    void fetch("/api/me/invitations", { signal: controller.signal })
      .then((response) => (response.ok ? response.json() : null))
      .then((payload: PendingAccess | null) => {
        if (payload) setPendingAccess(payload);
      })
      .catch(() => {
        // セッション切れなどでは案内文だけを出す。
      });

    return () => controller.abort();
  }, [code]);

  const handleAccept = async (invitationId: string) => {
    setAccepting(true);
    setAcceptError(null);

    try {
      const response = await fetch(
        `/api/me/invitations/${encodeURIComponent(invitationId)}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ status: "accepted" }),
        },
      );
      if (!response.ok) {
        setAcceptError((await readInvitationErrorMessage(response)).message);
        setAccepting(false);
        return;
      }
      window.location.assign("/");
    } catch {
      setAcceptError(INVITATION_ACCEPT_FALLBACK_MESSAGE);
      setAccepting(false);
    }
  };

  const handleSwitchAccount = async () => {
    setSwitchingAccount(true);
    try {
      await authClient.signOut();
    } finally {
      window.location.assign(MAZRICA_ACCOUNT_SWITCH.loginPath);
    }
  };

  const currentEmail = pendingAccess?.email ?? session?.user.email ?? null;
  const invitation =
    code === LOGIN_ERROR_CODE.NO_TENANT_MEMBERSHIP
      ? (pendingAccess?.invitation ?? null)
      : null;
  const expiresAtLabel = invitation ? formatExpiry(invitation.expiresAt) : null;

  return (
    <div
      role="alert"
      className="space-y-3 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-3 text-sm text-destructive"
    >
      <p>{ACCESS_DENIED_HEADLINES[code]}</p>

      {currentEmail && (
        <p className="text-foreground/80">
          現在 <span className="font-medium">{currentEmail}</span> でログイン
          しています。
        </p>
      )}

      {invitation ? (
        <div className="space-y-2">
          <p className="text-foreground/80">
            <span className="font-medium">{invitation.organizationName}</span>{" "}
            への招待が届いています。
            {expiresAtLabel && `（有効期限: ${expiresAtLabel}）`}
          </p>
          <Button
            type="button"
            className="w-full cursor-pointer"
            disabled={accepting}
            aria-busy={accepting}
            onClick={() => void handleAccept(invitation.id)}
          >
            {accepting ? (
              <PendingText>招待を受諾しています…</PendingText>
            ) : (
              "招待を受諾して利用を開始"
            )}
          </Button>
          {acceptError && <p role="alert">{acceptError}</p>}
        </div>
      ) : (
        <p className="text-foreground/80">{ACCESS_DENIED_DETAILS[code]}</p>
      )}

      {currentEmail ? (
        <Button
          type="button"
          variant="secondary"
          className="w-full cursor-pointer"
          disabled={switchingAccount}
          aria-busy={switchingAccount}
          onClick={() => void handleSwitchAccount()}
        >
          {switchingAccount ? (
            <PendingText>ログアウトしています…</PendingText>
          ) : (
            "別のアカウントを利用する"
          )}
        </Button>
      ) : (
        !isSessionPending && (
          <a
            href="/auth/login"
            className={buttonVariants({
              variant: "secondary",
              className: "w-full cursor-pointer",
            })}
          >
            ログイン画面へ戻る
          </a>
        )
      )}
    </div>
  );
}
